import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, Alert, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  MenuItem, Tooltip, InputAdornment, TablePagination,
} from '@mui/material';
import { AccountBalance, CheckCircle, Cancel, Search } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { disbursementService, paymentService, paymentRequestService, expenseService, projectService, farmOperationService } from '@smartgrow/shared';
import type { DisbursementItem } from '@smartgrow/shared';
import type { PaymentMethod } from '@smartgrow/shared/types';

interface DisburseForm {
  amount: number;
  paymentDate: string;
  paymentMethod: PaymentMethod;
  referenceNumber: string;
  notes: string;
}

const today = new Date().toISOString().split('T')[0];

const emptyForm = (): DisburseForm => ({
  amount: 0,
  paymentDate: today,
  paymentMethod: 'BANK_TRANSFER',
  referenceNumber: '',
  notes: '',
});

export const DisbursementPage = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [dialog, setDialog] = useState<{ open: boolean; item: DisbursementItem | null }>({
    open: false, item: null,
  });
  const [form, setForm] = useState<DisburseForm>(emptyForm());
  const [errors, setErrors] = useState<Partial<Record<keyof DisburseForm, string>>>({});
  const [cancelError, setCancelError] = useState('');
  const [rejectDialog, setRejectDialog] = useState<{ open: boolean; item: DisbursementItem | null }>({
    open: false, item: null,
  });
  const [rejectReason, setRejectReason] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [selectedOperationId, setSelectedOperationId] = useState('');
  const [selectedTaskId, setSelectedTaskId] = useState('');
  const [pendingPage, setPendingPage] = useState(0);
  const [historyPage, setHistoryPage] = useState(0);

  const userData = JSON.parse(localStorage.getItem('user_data') ?? '{}');
  const isAdminOrOwner = userData?.role === 'ADMIN' || userData?.role === 'OWNER';

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setPendingPage(0); setHistoryPage(0); }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const { data: projectsData } = useQuery({
    queryKey: ['projects-for-filter'],
    queryFn: () => projectService.getProjects({ size: 100 }),
  });

  const { data: operationsData } = useQuery({
    queryKey: ['operations-for-filter', selectedProjectId],
    queryFn: () => farmOperationService.getByProject(selectedProjectId),
    enabled: !!selectedProjectId,
  });

  const { data: tasksData } = useQuery({
    queryKey: ['tasks-for-filter', selectedOperationId],
    queryFn: () => farmOperationService.getTasksByOperation(selectedOperationId),
    enabled: !!selectedOperationId,
  });

  const filterParams = {
    search: debouncedSearch || undefined,
    projectId: selectedProjectId || undefined,
    farmOperationId: selectedOperationId || undefined,
    operationTaskId: selectedTaskId || undefined,
  };

  const { data: pendingData, isLoading, isError } = useQuery({
    queryKey: ['disbursements', 'pending', pendingPage, filterParams],
    queryFn: () => disbursementService.getPending({ page: pendingPage, size: 10, ...filterParams }),
  });

  const { data: historyData } = useQuery({
    queryKey: ['disbursements', 'history', historyPage, filterParams],
    queryFn: () => disbursementService.getHistory({ page: historyPage, size: 10, ...filterParams }),
  });

  const handleProjectChange = (value: string) => {
    setSelectedProjectId(value);
    setSelectedOperationId('');
    setSelectedTaskId('');
    setPendingPage(0);
    setHistoryPage(0);
  };

  const handleOperationChange = (value: string) => {
    setSelectedOperationId(value);
    setSelectedTaskId('');
    setPendingPage(0);
    setHistoryPage(0);
  };

  const pending: DisbursementItem[] = pendingData?.content ?? [];
  const history: DisbursementItem[] = historyData?.content ?? [];

  const { data: summary } = useQuery({
    queryKey: ['disbursements', 'summary', filterParams],
    queryFn: () => disbursementService.getSummary(filterParams),
  });

  const disburseMutation = useMutation({
    mutationFn: ({ item, data }: { item: DisbursementItem; data: DisburseForm }) => {
      const payload = {
        amount: data.amount,
        paymentDate: data.paymentDate,
        paymentMethod: data.paymentMethod,
        referenceNumber: data.referenceNumber || undefined,
        notes: data.notes || undefined,
      };
      if (item.source === 'EXPENSE') {
        return paymentService.processExpensePayment({ expenseRequestId: item.id, ...payload });
      }
      return item.type === 'PROCUREMENT'
        ? paymentService.disburseProcurementPayment(item.id, payload)
        : paymentService.disburseContractorPayment(item.id, payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['disbursements'] });
      qc.invalidateQueries({ queryKey: ['payment-requests'] });
      qc.invalidateQueries({ queryKey: ['expenses'] });
      qc.invalidateQueries({ queryKey: ['expense'] });
      setDialog({ open: false, item: null });
      setForm(emptyForm());
    },
  });

  const cancelMutation = useMutation({
    mutationFn: (paymentId: string) => paymentService.cancelPayment(paymentId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['disbursements'] });
      qc.invalidateQueries({ queryKey: ['payment-requests'] });
      qc.invalidateQueries({ queryKey: ['expenses'] });
      qc.invalidateQueries({ queryKey: ['expense'] });
      setCancelError('');
    },
    onError: () => setCancelError('Failed to cancel disbursement.'),
  });

  const rejectMutation = useMutation({
    mutationFn: async ({ item, reason }: { item: DisbursementItem; reason: string }) => {
      if (item.source === 'EXPENSE') {
        await expenseService.reject(item.id, reason);
      } else {
        await paymentRequestService.reject(item.id, reason);
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['disbursements'] });
      qc.invalidateQueries({ queryKey: ['payment-requests'] });
      qc.invalidateQueries({ queryKey: ['expenses'] });
      qc.invalidateQueries({ queryKey: ['expense'] });
      setRejectDialog({ open: false, item: null });
      setRejectReason('');
    },
  });

  const openDialog = (item: DisbursementItem) => {
    setForm({ ...emptyForm(), amount: item.amount });
    setDialog({ open: true, item });
    setErrors({});
  };

  const validate = () => {
    const e: typeof errors = {};
    if (!form.amount || Number(form.amount) <= 0) e.amount = 'Amount must be greater than 0';
    if (!form.paymentDate) e.paymentDate = 'Payment date is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const set = (field: keyof DisburseForm, value: any) => {
    setForm((f) => ({ ...f, [field]: value }));
    setErrors((e) => ({ ...e, [field]: undefined }));
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" sx={{ mb: 3 }}>Fund Disbursement</Typography>

      {/* Summary cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        {[
          { label: 'Awaiting Disbursement', value: summary?.awaitingTotal ?? 0, color: 'warning.main' },
          { label: 'Total Disbursed', value: summary?.disbursedTotal ?? 0, color: 'success.main' },
          { label: 'Pending Requests', value: summary?.pendingCount ?? 0, isCurrency: false, color: 'info.main' },
        ].map(({ label, value, color, isCurrency = true }) => (
          <Paper key={label} sx={{ p: 2.5, flex: '1 1 200px', borderLeft: 4, borderColor: color }}>
            <Typography variant="body2" color="text.secondary">{label}</Typography>
            <Typography variant="h5" fontWeight="bold" color={color}>
              {isCurrency ? `₦${Number(value).toLocaleString()}` : value}
            </Typography>
          </Paper>
        ))}
      </Box>

      {isError && <Alert severity="error" sx={{ mb: 2 }}>Failed to load pending disbursements.</Alert>}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <TextField select size="small" label="Project" value={selectedProjectId}
            onChange={(e) => handleProjectChange(e.target.value)} sx={{ minWidth: 180 }}>
            <MenuItem value="">All Projects</MenuItem>
            {(projectsData?.content ?? []).map((p) => (
              <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
            ))}
          </TextField>
          <TextField select size="small" label="Operation" value={selectedOperationId}
            onChange={(e) => handleOperationChange(e.target.value)} disabled={!selectedProjectId} sx={{ minWidth: 180 }}>
            <MenuItem value="">All Operations</MenuItem>
            {(operationsData?.content ?? []).map((op) => (
              <MenuItem key={op.id} value={op.id}>{op.name}</MenuItem>
            ))}
          </TextField>
          <TextField select size="small" label="Task" value={selectedTaskId}
            onChange={(e) => setSelectedTaskId(e.target.value)} disabled={!selectedOperationId} sx={{ minWidth: 180 }}>
            <MenuItem value="">All Tasks</MenuItem>
            {(tasksData ?? []).map((t) => (
              <MenuItem key={t.id} value={t.id}>{t.title}</MenuItem>
            ))}
          </TextField>
        </Box>
        <TextField fullWidth placeholder="Search by request #, description, contractor, or project..." value={search}
          onChange={(e) => setSearch(e.target.value)} size="small"
          InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }} />
      </Paper>

      <Typography variant="h6" sx={{ mb: 1.5 }}>Approved Requests — Ready to Disburse</Typography>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Request #</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Requested By</TableCell>
              <TableCell>Contractor / Project</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Approved</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : pending.length ? (
              pending.map((item) => (
                <TableRow key={`${item.source}-${item.id}`} hover>
                  <TableCell><Typography variant="body2" fontFamily="monospace">{item.requestNumber}</Typography></TableCell>
                  <TableCell>
                    <Chip
                      label={item.type}
                      variant="outlined"
                      size="small"
                      color={item.source === 'EXPENSE' ? 'secondary' : 'default'}
                    />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 200 }}>
                    <Tooltip title={item.description}>
                      <Typography variant="body2" noWrap>{item.description}</Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{item.requestedByName}</TableCell>
                  <TableCell>
                    {item.contractorName && <Typography variant="body2">{item.contractorName}</Typography>}
                    {item.projectName && (
                      <Typography variant="caption" color="text.secondary"
                        sx={item.projectId ? { cursor: 'pointer', '&:hover': { textDecoration: 'underline' } } : undefined}
                        onClick={() => item.projectId && navigate(`/dashboard/projects/${item.projectId}`)}
                      >
                        {item.projectName}
                      </Typography>
                    )}
                    {!item.contractorName && !item.projectName && '-'}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">₦{item.amount.toLocaleString()}</Typography>
                  </TableCell>
                  <TableCell>
                    {item.approvalDate ? new Date(item.approvalDate).toLocaleDateString() : '-'}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        startIcon={<AccountBalance />}
                        onClick={() => openDialog(item)}
                      >
                        Disburse
                      </Button>
                      {isAdminOrOwner && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<Cancel fontSize="small" />}
                          onClick={() => { setRejectDialog({ open: true, item }); setRejectReason(''); }}
                        >
                          Cancel
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                    No approved requests awaiting disbursement
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={pendingData?.totalElements ?? 0}
          page={pendingPage}
          onPageChange={(_e, newPage) => setPendingPage(newPage)}
          rowsPerPage={10}
          rowsPerPageOptions={[10]}
        />
      </TableContainer>

      {/* Paid history */}
      <Box sx={{ mt: 4 }}>
          <Typography variant="h6" sx={{ mb: 1.5 }}>Disbursed History</Typography>
          {cancelError && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setCancelError('')}>{cancelError}</Alert>}
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Request #</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Requested By</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Status</TableCell>
                  {isAdminOrOwner && <TableCell>Action</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {history.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isAdminOrOwner ? 7 : 6} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                        No disbursement history found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : history.map((item) => (
                  <TableRow key={`${item.source}-${item.id}`} hover>
                    <TableCell><Typography variant="body2" fontFamily="monospace">{item.requestNumber}</Typography></TableCell>
                    <TableCell>
                      <Chip
                        label={item.type}
                        variant="outlined"
                        size="small"
                        color={item.source === 'EXPENSE' ? 'secondary' : 'default'}
                      />
                    </TableCell>
                    <TableCell><Typography variant="body2" noWrap sx={{ maxWidth: 250 }}>{item.description}</Typography></TableCell>
                    <TableCell>{item.requestedByName}</TableCell>
                    <TableCell><Typography variant="body2" fontWeight="bold">₦{item.amount.toLocaleString()}</Typography></TableCell>
                    <TableCell>
                      <Chip icon={<CheckCircle />} label="Disbursed" color="success" size="small" />
                    </TableCell>
                    {isAdminOrOwner && (
                      <TableCell>
                        {item.paymentId && (
                          <Button
                            size="small"
                            color="error"
                            variant="outlined"
                            startIcon={<Cancel fontSize="small" />}
                            disabled={cancelMutation.isPending}
                            onClick={() => {
                              if (window.confirm(`Cancel disbursement for ${item.requestNumber}? This will revert it back to Approved and awaiting disbursement.`)) {
                                cancelMutation.mutate(item.paymentId!);
                              }
                            }}
                          >
                            Cancel
                          </Button>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={historyData?.totalElements ?? 0}
              page={historyPage}
              onPageChange={(_e, newPage) => setHistoryPage(newPage)}
              rowsPerPage={10}
              rowsPerPageOptions={[10]}
            />
          </TableContainer>
        </Box>

      {/* Disburse Dialog */}
      <Dialog open={dialog.open} onClose={() => setDialog({ open: false, item: null })} fullWidth maxWidth="sm">
        <DialogTitle>
          Disburse Funds — {dialog.item?.requestNumber}
        </DialogTitle>
        <DialogContent>
          {dialog.item && (
            <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">Description</Typography>
              <Typography variant="body2">{dialog.item.description}</Typography>
              {dialog.item.contractorName && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Contractor: {dialog.item.contractorName}
                </Typography>
              )}
              {dialog.item.source === 'EXPENSE' && dialog.item.categoryName && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Expense — {dialog.item.categoryName}
                </Typography>
              )}
              <Typography variant="body2" fontWeight="bold" sx={{ mt: 0.5 }}>
                Requested: ₦{dialog.item.amount.toLocaleString()}
              </Typography>
            </Box>
          )}

          {disburseMutation.isError && (
            <Alert severity="error" sx={{ mb: 2 }}>Disbursement failed. Please try again.</Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Amount to Disburse (₦)"
              type="number"
              value={form.amount || ''}
              onChange={(e) => set('amount', parseFloat(e.target.value) || 0)}
              fullWidth error={!!errors.amount} helperText={errors.amount}
              inputProps={{ min: 0.01, step: 0.01 }}
            />
            <TextField
              label="Payment Date"
              type="date"
              value={form.paymentDate}
              onChange={(e) => set('paymentDate', e.target.value)}
              fullWidth error={!!errors.paymentDate} helperText={errors.paymentDate}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              select
              label="Payment Method"
              value={form.paymentMethod}
              onChange={(e) => set('paymentMethod', e.target.value as PaymentMethod)}
              fullWidth
            >
              <MenuItem value="BANK_TRANSFER">Bank Transfer</MenuItem>
              <MenuItem value="CASH">Cash</MenuItem>
              <MenuItem value="MOBILE_MONEY">Mobile Money</MenuItem>
              <MenuItem value="CHEQUE">Cheque</MenuItem>
            </TextField>
            <TextField
              label="Reference Number (optional)"
              value={form.referenceNumber}
              onChange={(e) => set('referenceNumber', e.target.value)}
              fullWidth
            />
            <TextField
              label="Notes (optional)"
              multiline rows={2}
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog({ open: false, item: null })}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            disabled={disburseMutation.isPending}
            startIcon={disburseMutation.isPending ? <CircularProgress size={16} /> : <AccountBalance />}
            onClick={() => {
              if (validate() && dialog.item) {
                disburseMutation.mutate({ item: dialog.item, data: form });
              }
            }}
          >
            Confirm Disbursement
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel (reject an approved-but-undisbursed request) Dialog */}
      <Dialog open={rejectDialog.open} onClose={() => setRejectDialog({ open: false, item: null })} fullWidth maxWidth="sm">
        <DialogTitle>Cancel Disbursement — {rejectDialog.item?.requestNumber}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This request is already approved but has not been disbursed. Cancelling it reverts it to Rejected —
            it will no longer be awaiting disbursement.
          </Typography>
          {rejectMutation.isError && (
            <Alert severity="error" sx={{ mb: 2 }}>Failed to cancel this request. Please try again.</Alert>
          )}
          <TextField
            label="Reason (optional)"
            multiline rows={2}
            fullWidth
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog({ open: false, item: null })}>Back</Button>
          <Button
            variant="contained"
            color="error"
            disabled={rejectMutation.isPending}
            startIcon={rejectMutation.isPending ? <CircularProgress size={16} /> : <Cancel />}
            onClick={() => {
              if (rejectDialog.item) {
                rejectMutation.mutate({ item: rejectDialog.item, reason: rejectReason || 'Cancelled before disbursement' });
              }
            }}
          >
            Confirm Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
